import React, { useState } from "react";
import { X, LogIn, User, Cloud, Smartphone } from "lucide-react";

interface AuthChoiceModalProps {
  onClose: () => void;
  onAnonymousLogin: () => void;
  onGoogleLogin: () => void;
}

export const AuthChoiceModal = ({
  onClose,
  onAnonymousLogin,
  onGoogleLogin,
}: AuthChoiceModalProps) => {
  const [rememberChoice, setRememberChoice] = useState(false);

  const handleAnonymousChoice = () => {
    if (rememberChoice) {
      localStorage.setItem("authChoice", "anonymous");
    }
    onAnonymousLogin();
  };

  const handleGoogleChoice = () => {
    if (rememberChoice) {
      localStorage.setItem("authChoice", "google");
    }
    onGoogleLogin();
  };

  const handleDismiss = () => {
    if (rememberChoice) {
      localStorage.setItem("authChoice", "dismissed");
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      <div className="ledger-shell max-w-lg w-full text-sm">
        {/* Header */}
        <div className="flex justify-between items-center p-4">
          <h2 className="text-base font-semibold uppercase tracking-wider">Choose Your Experience</h2>
          <button
            onClick={handleDismiss}
            className="text-[#E6E5D3] hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="text-center">
            <p className="text-[#E6E5D3] text-sm mb-1">
              Welcome to Trench Crusade Warband Manager!
            </p>
            <p className="text-[#E6E5D3] text-xs opacity-80">
              Choose how you'd like to save your warband data.
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4">
            {/* Google Sign-in Option */}
            <div className="ledger-panel p-4">
              <div className="flex items-start gap-4">
                <div className="bg-[#1e40af] p-2">
                  <Cloud size={20} className="text-[#E6E5D3]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-[#111] mb-2">
                    Online account
                  </h3>
                  <ul className="text-xs text-[#777] space-y-1 mb-3">
                    <li>Save your warband across devices</li>
                    <li>Sync data between browsers</li>
                    <li>Access from anywhere</li>
                    <li>Automatic backups</li>
                  </ul>
                  <button
                    onClick={handleGoogleChoice}
                    className="ledger-btn px-3 py-2 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold"
                  >
                    <LogIn size={16} />
                    Sign in with Google
                  </button>
                </div>
              </div>
            </div>

            {/* Anonymous Option */}
            <div className="ledger-panel p-4">
              <div className="flex items-start gap-4">
                <div className="bg-[#222] p-2">
                  <Smartphone size={20} className="text-[#E6E5D3]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-[#111] mb-2">
                    Offline mode
                  </h3>
                  <ul className="text-xs text-[#777] space-y-1 mb-3">
                    <li>Save locally on this device only</li>
                    <li>No account required</li>
                    <li>Data stays in your browser</li>
                    <li>Limited to this device/browser</li>
                  </ul>
                  <button
                    onClick={handleAnonymousChoice}
                    className="ledger-btn px-3 py-2 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold"
                  >
                    <User size={16} />
                    Continue Offline
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Remember Choice Checkbox */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-700">
            <input
              type="checkbox"
              id="rememberChoice"
              checked={rememberChoice}
              onChange={(e) => setRememberChoice(e.target.checked)}
              className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 focus:ring-2"
            />
            <label htmlFor="rememberChoice" className="text-gray-300 text-sm">
              Remember my choice and don't show this again
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={handleDismiss}
            className="w-full text-gray-400 hover:text-gray-300 text-sm underline transition-colors"
          >
            Skip for now (you can change this later in settings)
          </button>
        </div>
      </div>
    </div>
  );
};
